import { useState } from 'react';
import { Grid, Button, useTheme } from '@simplywallst/ui-core';
import ActionSheet, { Background, Sheet } from '@components/ActionSheet';

import WelcomeStep from './WelcomeStep';
import CreateProfileStep from './CreateProfileStep';
import Dot from './Dot';
import useUser from '@/hooks/useUser';
import { UserHttpError } from '@/hooks/useUser/userHttpErrors';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

const NarrativeOnboardingModal = ({ isOpen, onClose }: Props) => {
  const [step, setStep] = useState(0);
  const [hasError, setHasError] = useState(false);
  const theme = useTheme();
  const [username, setUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const {
    data: user,
    mutate,
    mutationStatus,
  } = useUser({
    onMutationSuccess: () => {
      setHasError(false);
      onClose();
    },
    onMutationError: (mutationError: unknown) => {
      if (mutationError instanceof UserHttpError) {
        setErrorMessage('Display name already taken');
        setHasError(true);
      } else {
        setErrorMessage('The operation failed.');
        setHasError(true);
      }
    },
  });

  const updateUserDisplayName = () => {
    if (!hasError && username !== user?.displayName && username.length > 0) {
      setErrorMessage(undefined);
      mutate({ display_name: username });
    }
  };

  return (
    <ActionSheet open={isOpen} onClose={onClose}>
      <Background onClick={onClose} />
      <Sheet
        sheetLayout={{
          'max-width': '698px',
          height: 'max-content',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Grid
          paddingX="x2"
          paddingXMd="x0"
          data-testid="narrative-onboarding-modal"
        >
          <Grid
            backgroundColor="charcoal"
            paddingX="x2"
            paddingY="x6"
            paddingXMd="x13"
            paddingYMd="x9"
            borderRadius="x1"
            boxShadow="subtleBorder"
            autoRows={'max-content'}
            gap={`${theme.x3}px`}
          >
            {step === 0 && <WelcomeStep />}
            {step === 1 && (
              <CreateProfileStep
                setHasError={setHasError}
                username={username}
                mutationStatus={mutationStatus}
                resetMutationError={() => setErrorMessage(undefined)}
                mutationErrorMessage={errorMessage}
                setUsername={setUsername}
              />
            )}
            <Grid justifyContent="center">
              <Grid gap={`${theme.x2}px`} width="200px">
                <Button
                  styleType="louderLarge"
                  disabled={step === 1 ? hasError : false}
                  onClick={() => {
                    if (step === 0) {
                      setStep(1);
                    } else {
                      updateUserDisplayName();
                    }
                  }}
                  name="next"
                >
                  Next
                </Button>
                <Grid
                  justifyContent="center"
                  gap={`${theme.x1}px`}
                  templateColumns="max-content max-content"
                >
                  <Dot onClick={() => setStep(0)} active={step === 0} />
                  <Dot onClick={() => setStep(1)} active={step === 1} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Sheet>
    </ActionSheet>
  );
};

export default NarrativeOnboardingModal;
